import type { BadgeTone } from '../components/ui/index.ts';
/** Shared display-label functions for enum/status values shown across the UI. */

import type { AddressKind, AddressSubjectKind, CaseSetting } from '../api/postal-addresses.ts';
import type { SeveredMember } from '../api/cases.ts';

/**
 * How the person receiving the service is named in forms. ONE home, because the
 * same field was labelled three different ways — "Service recipient first name"
 * (enquiry wizard), "Recipient first name" (enquiry edit) and "Client first name"
 * (add existing client) — and the last collides with "client contact", a
 * DIFFERENT person shown in the same forms, so it read as the parent's name.
 *
 * "Service recipient" is the term the rest of the system already uses: the
 * consent-model and contact-relationship enums, the API field names, and the
 * `service_recipient` role. It is also discipline-neutral, which "client" is not
 * once the platform carries SLT, physio and psychology alongside ABA.
 */
export const SERVICE_RECIPIENT_LABELS = {
  firstName: 'Service recipient first name',
  lastName: 'Service recipient last name',
  dob: 'Service recipient date of birth',
  /** Used where the recipient consents for themselves, so the email is theirs. */
  ownEmail: "Service recipient's own email",
  /** Plain-language gloss for first use on a form; the term is jargon on its own. */
  hint: 'The person receiving support. Their contact, if they have one, is entered below.',
} as const;

export function statusLabel(s: string): string {
  switch (s) {
    case 'active': return 'Active';
    case 'paused': return 'Paused';
    case 'archived': return 'Archived';
    case 'discharged': return 'Discharged';
    case 'cancelled': return 'Cancelled';
    case 'cancellation_pending': return 'Cancellation pending';
    case 'on_hold': return 'On hold';
    case 'proposed': return 'Proposed';
    case 'mastered': return 'Mastered';
    case 'discontinued': return 'Discontinued';
    case 'draft': return 'Draft';
    case 'issued': return 'Issued';
    case 'sent': return 'Sent';
    case 'paid': return 'Paid';
    case 'void': return 'Void';
    case 'pending_response': return 'Awaiting response';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

export function caseTypeLabel(s: string): string {
  switch (s) {
    case 'child': return 'Child';
    case 'young_adult': return 'Young adult';
    case 'adult': return 'Adult';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

/** The six `encounters.encounter_type` values. Was a copy inside CaseSessionsScreen. */
export function encounterTypeLabel(s: string): string {
  switch (s) {
    case 'therapy_session': return 'Therapy session';
    case 'assessment': return 'Assessment';
    case 'supervision': return 'Supervision';
    case 'review': return 'Review';
    case 'admin_contact': return 'Admin contact';
    case 'home_practice': return 'Home practice';
    default: return s.replace(/_/g, ' ');
  }
}

export function consentModelLabel(s: string): string {
  switch (s) {
    case 'client_contact': return 'Someone consents on their behalf';
    case 'service_recipient': return 'They consent for themselves';
    case 'shared': return 'Consent is shared (young person in transition)';
    case 'best_interests': return 'They lack capacity: best-interests decision';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

/** Base endorsement-status wording (SPEC.md §21); screens add the practice name. */
export function endorsementStatusLabel(s: string): string {
  switch (s) {
    // NOT "awaiting verification": a practice is never required to confirm one, and many
    // never will (FB-0040). This is the honest present state, and the same word the client
    // is shown for the same row.
    case 'claimed': return 'Self-declared';
    case 'verified': return 'Verified';
    case 'rejected': return 'Rejected';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

export function practiceRoleLabel(s: string): string {
  switch (s) {
    case 'owner': return 'Practice owner';
    case 'practitioner': return 'Practitioner';
    case 'support_worker': return 'ABA Tutor';
    case 'client_contact': return 'Client';
    case 'admin': return 'Admin';
    case 'observer': return 'Observer';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

/** Account-level role (`users.role`) — a different axis from `practiceRoleLabel` above. */
export function accountRoleLabel(s: string): string {
  switch (s) {
    case 'platform_admin': return 'Platform admin';
    case 'member': return 'Member';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

export function disciplineLabel(s: string): string {
  switch (s) {
    case 'aba': return 'Applied Behaviour Analysis';
    case 'slt': return 'Speech & Language Therapy';
    case 'physio': return 'Physiotherapy';
    case 'ot': return 'Occupational Therapy';
    case 'psychology': return 'Psychology';
    default: return s.toUpperCase();
  }
}

export function contactRelationshipLabel(s: string): string {
  switch (s) {
    case 'parent': return 'Parent';
    case 'guardian': return 'Guardian';
    case 'service_recipient': return 'The recipient themselves';
    case 'deputy': return 'Deputy';
    case 'other': return 'Other';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

export function fundingSourceLabel(s: string): string {
  switch (s) {
    case 'ehcp_la': return 'EHCP / LA funded';
    case 'school': return 'School budget';
    case 'parent': return 'Funded by their contact';
    case 'mixed': return 'Mixed funding';
    case 'self': return 'Self-funded';
    case 'tbc': return 'Funding TBC';
    case 'other': return 'Other';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

export function referrerTypeLabel(s: string): string {
  switch (s) {
    case 'client': return 'Client referral';
    case 'organisation': return 'Organisation / setting';
    case 'self': return 'Self-referral';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

export function addressKindLabel(s: string): string {
  switch (s) {
    case 'home': return 'Home address';
    case 'service': return 'Service delivery address';
    case 'billing': return 'Invoice address';
    case 'correspondence': return 'Correspondence address';
    default: return s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

/**
 * Per-(subject, kind) label override (owner decision 2026-08-14) — e.g. an owner_practice
 * correspondence address is the "Registered address", not a generic "Correspondence
 * address". Falls back to `addressKindLabel` where no override applies.
 */
const ADDRESS_LABEL_OVERRIDES: Partial<Record<AddressSubjectKind, Partial<Record<AddressKind, string>>>> = {
  owner_practice: { correspondence: 'Registered address', service: 'Business address' },
  client_record: { correspondence: 'Client address' },
  enquiry: { correspondence: 'Client address' },
  service_recipient: { home: 'Home address' },
  payer: { billing: 'Invoice address' },
  case: { service: 'Service address' },
};

export function addressDisplayLabel(subjectKind: AddressSubjectKind, kind: AddressKind): string {
  return ADDRESS_LABEL_OVERRIDES[subjectKind]?.[kind] ?? addressKindLabel(kind);
}

/**
 * One line saying what a label MEANS, where the label alone is a term of art the owner has
 * to guess at. Lives beside `ADDRESS_LABEL_OVERRIDES` for the same reason the labels do:
 * the vocabulary of addresses has one home, and a screen that renders a kind renders its
 * hint without restating it. Absent = the label speaks for itself.
 */
const ADDRESS_HINTS: Partial<Record<AddressSubjectKind, Partial<Record<AddressKind, string>>>> = {
  owner_practice: {
    correspondence: 'Your official address. Printed on every invoice and client contract you issue.',
    service: 'Only if you deliver services from somewhere else, such as a clinic or an office that is not your registered address. Leave it out if they are the same place.',
  },
};

export function addressDisplayHint(
  subjectKind: AddressSubjectKind,
  kind: AddressKind,
): string | null {
  return ADDRESS_HINTS[subjectKind]?.[kind] ?? null;
}

/** A case's own service address is titled by WHERE it is delivered, not by its (single)
 *  kind — several may be active at once, one per setting. */
export function caseSettingLabel(setting: string): string {
  switch (setting) {
    case 'home': return 'Home';
    case 'school': return 'School';
    case 'clinic': return 'Clinic';
    case 'community': return 'Community';
    case 'remote': return 'Remote';
    default: return setting.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  }
}

// Re-exported so callers that only need the setting type do not have to reach into the API module.
export type { CaseSetting };

/** What a handover removed, for the actor who asked for it. */
export function severedMessage(severed: readonly SeveredMember[]): string | null {
  if (severed.length === 0) return null;
  const names = severed.map((m) => m.displayName ?? 'a team member').join(', ');
  return `${String(severed.length)} person${severed.length === 1 ? '' : 's'} lost access to this case: ${names}.`;
}

/** Lifecycle tone for a case status. Vocabulary is the `cases.status` CHECK constraint. */
export function statusTone(status: string): BadgeTone {
  if (status === 'active') return 'good';
  if (status === 'paused' || status === 'on_hold') return 'watch';
  if (status === 'cancellation_pending') return 'danger';
  return 'neutral';
}

/** "today" / "3 days ago" / a date, from an epoch-ms timestamp. */
export function relativeDay(at: number | null): string | null {
  if (at === null) return null;
  const days = Math.floor((Date.now() - at) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${String(days)} days ago`;
  return new Date(at).toLocaleDateString('en-GB');
}

/** yyyy-mm-dd for today, local time - a date input's max and default. */
export function todayInputValue(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
